import { Box, Skeleton } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * The Review Lot & NDC grid while its data is in flight.
 *
 * Replaces two grey blocks that said only "something is coming". This says WHAT is coming: the
 * real header in the real order, over rows shaped like the rows that will land — a badge where
 * the facility avatar goes, stacked bars where a cell puts a caption under its value. The layout
 * does not jump when the payload arrives, which is the whole reason to draw a skeleton instead of
 * a spinner.
 *
 * The columns are declared here rather than derived from `buildColumnDefs`: that module pulls in
 * AG Grid, and the dialog's own tests stub it out — importing it would take the skeleton down
 * with it. `BulkLotNdcReviewSkeleton.tests.js` asserts this list against the real column defs
 * instead, so the two cannot drift apart unnoticed.
 *
 * `track` mirrors each column's own sizing: a fixed `width` stays fixed, a `flex` becomes that
 * many `fr`, and everything else gets a `minmax` off its `minWidth`.
 */
const REVIEW_COLUMNS = [
  { label: 'Order Id', track: 'minmax(130px, 1fr)' },
  { label: 'Rx Number', track: 'minmax(115px, 1fr)' },
  { label: 'Facility', track: '92px', shape: 'badge' },
  { label: 'Drug name and strength', track: 'minmax(210px, 2fr)', shape: 'stacked' },
  { label: 'Qty', track: '74px' },
  { label: 'Partner', track: 'minmax(130px, 1fr)' },
  { label: 'NDC', track: 'minmax(175px, 1fr)', shape: 'value-with-action' },
  { label: 'Lot', track: 'minmax(200px, 1.4fr)', shape: 'stacked' },
  { label: 'Status', track: 'minmax(230px, 1fr)', shape: 'chip' },
];

const TEMPLATE = REVIEW_COLUMNS.map(({ track }) => track).join(' ');

/** Matches the grid's own `rowHeight`, so the rows do not resize under the user. */
const ROW_HEIGHT = 76;

/** Never more skeleton rows than there are items — eight placeholders over a batch of two lies. */
const MAX_ROWS = 8;

/**
 * Deterministic, not random: a fresh `Math.random()` on every render made the bars twitch while
 * the request was still running. Indexing by row and column gives the ragged right edge real text
 * has, and gives it the same way every time.
 */
const BAR_WIDTHS = ['78%', '62%', '88%', '70%', '84%', '58%'];
const barWidth = (row, column) => BAR_WIDTHS[(row * 3 + column) % BAR_WIDTHS.length];

const bar = (width, height = 12) => (
  <Skeleton variant='rounded' height={height} width={width} sx={{ borderRadius: 0.75 }} />
);

const SkeletonCell = ({ shape, row, column }) => {
  if (shape === 'badge') {
    return <Skeleton variant='circular' width={26} height={26} />;
  }

  if (shape === 'stacked') {
    return (
      <Box sx={{ width: '100%', display: 'grid', gap: 0.75 }}>
        {bar(barWidth(row, column))}
        {bar(barWidth(row + 1, column), 9)}
      </Box>
    );
  }

  if (shape === 'value-with-action') {
    return (
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
        {bar(barWidth(row, column))}
        <Skeleton variant='rounded' width={18} height={18} sx={{ flexShrink: 0 }} />
      </Box>
    );
  }

  if (shape === 'chip') {
    return (
      <Box sx={{ width: '100%', display: 'grid', gap: 0.75, justifyItems: 'start' }}>
        <Skeleton variant='rounded' width={96} height={20} sx={{ borderRadius: 5 }} />
        {bar(barWidth(row + 2, column), 9)}
      </Box>
    );
  }

  return <Box sx={{ width: '100%' }}>{bar(barWidth(row, column))}</Box>;
};

SkeletonCell.propTypes = {
  shape: PropTypes.string,
  row: PropTypes.number.isRequired,
  column: PropTypes.number.isRequired,
};

SkeletonCell.defaultProps = { shape: null };

const BulkLotNdcReviewSkeleton = ({ itemCount }) => {
  const rows = Math.max(1, Math.min(itemCount || MAX_ROWS, MAX_ROWS));

  return (
    <Box
      // aria-hidden with a status role beside it: a screen reader gets one "Loading" rather than
      // nine rows of meaningless placeholder.
      role='status'
      aria-label='Loading the orders to review'
      sx={{
        flex: 1,
        minHeight: 280,
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        // A placeholder must never be the thing that makes the dialog scroll sideways.
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: TEMPLATE,
          gap: 2,
          px: 2,
          py: 1.5,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          fontSize: '0.8125rem',
          fontWeight: 600,
        }}
      >
        {REVIEW_COLUMNS.map(({ label }) => (
          <Box key={label} sx={{ minWidth: 0 }}>
            {label}
          </Box>
        ))}
      </Box>

      {Array.from({ length: rows }, (unused, row) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={`skeleton-row-${row}`}
          aria-hidden='true'
          // The rows carry no role and no text by design, so there is nothing for a Testing
          // Library query to reach them by. This is the hook the row-count tests use.
          data-testid='bulk-lot-ndc-skeleton-row'
          sx={{
            display: 'grid',
            gridTemplateColumns: TEMPLATE,
            alignItems: 'center',
            gap: 2,
            px: 2,
            height: ROW_HEIGHT,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-of-type': { borderBottom: 'none' },
          }}
        >
          {REVIEW_COLUMNS.map(({ label, shape }, column) => (
            <SkeletonCell key={label} shape={shape} row={row} column={column} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

BulkLotNdcReviewSkeleton.propTypes = {
  /** How many rows the review was opened for, so the placeholder does not over-promise. */
  itemCount: PropTypes.number,
};

BulkLotNdcReviewSkeleton.defaultProps = { itemCount: MAX_ROWS };

export { REVIEW_COLUMNS };
export default BulkLotNdcReviewSkeleton;
